import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Accordion, Button, ButtonGroup } from "aible";
import styles from "./OpenShellCreate.module.css";
import { useDispatch, useSelector } from "@/types/store";
import { getAPIToken } from "utils/request";
import { AibleClientFactory } from "client/aible/factory";
import PolicyEditor, { ChannelsList, DEFAULT_POLICY, ProvidersList } from "./PolicyEditor";
import Select from "react-select";
import { skillsContent } from "./skillsContent";
import { ClawTaskItem } from "components/Templates/AgentC";
import FlaggedNetworkRequests from "components/Openshell/FlaggedNetworkRequests";
import * as aiServiceModelsReducer from "containers/AiService/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import { AIBLE_TOOLS, selectToolsDeny, selectIsOpenClawConnected, selectOpenClawConnectionStatus, patchOpenClawToolsDeny, selectEligibleSkills, selectMissingRequirements, selectSessionsUsage, fetchSessionsUsage, selectAgentFileContent, selectAgentFileDraft, selectAgentFileHasUnsavedChanges, setAgentFileDraft, fetchAgentFile, saveAgentFile, handleOpenClaw, selectPlugins } from "containers/OpenClaw";
import { connectToOpenClaw, disconnectFromOpenClaw } from "containers/OpenClaw/openClawThunk";
import * as foldersReducer from "@/containers/Folders/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import { OpenShellAgentBuilder } from "@/components/Openshell/OpenShellAgentBuilder";
import SandboxWorkList from "@/components/Openshell/SandboxWorkList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faBan } from "@fortawesome/free-solid-svg-icons";
import SkillsCatalog from "@/components/Openshell/SkillsCatalog";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
// ── react-select custom styles to match our theme ──────────────────────
const selectStyles = {
    control: (base, state) => ({
        ...base,
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: "14px",
        background: state.isDisabled ? "var(--lighter-grey)" : "#ffffff",
        borderColor: state.isDisabled ? "#dcdcdc" : state.isFocused ? "#7c5cfc" : "#e0dce8",
        borderWidth: "1.5px",
        borderRadius: "10px",
        padding: "2px 4px",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(124, 92, 252, 0.1)" : "none",
        cursor: state.isDisabled ? "not-allowed" : "default",
        "&:hover": {
            borderColor: state.isDisabled ? "#dcdcdc" : state.isFocused ? "#7c5cfc" : "#c9c1e0"
        },
        minHeight: "42px"
    }),
    option: (base, state) => ({
        ...base,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "13.5px",
        color: state.isSelected ? "#fff" : "#1a1630",
        background: state.isSelected ? "#7c5cfc" : state.isFocused ? "#f4f2f9" : "#fff",
        "&:active": { background: "#ebe7fc" }
    }),
    singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled ? "var(--grey)" : "#1a1630"
    }),
    placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled ? "var(--grey)" : "#b8b3c6"
    }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isDisabled ? "var(--grey)" : base.color,
        cursor: state.isDisabled ? "not-allowed" : "default"
    }),
    indicatorSeparator: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled ? "#dcdcdc" : base.backgroundColor
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "10px",
        border: "1.5px solid #e0dce8",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        overflow: "hidden"
    }),
    menuList: (base) => ({
        ...base,
        padding: "4px 0"
    })
};
const CHANNEL_PROFILES = {
    slack: {
        name: "slack",
        endpoints: [
            {
                host: "slack.com",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                request_body_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "POST",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "api.slack.com",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                request_body_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "POST",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "hooks.slack.com",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                request_body_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "POST",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "wss-primary.slack.com",
                port: 443,
                protocol: "websocket",
                enforcement: "enforce",
                websocket_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "WEBSOCKET_TEXT",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "wss-backup.slack.com",
                port: 443,
                protocol: "websocket",
                enforcement: "enforce",
                websocket_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "WEBSOCKET_TEXT",
                            path: "/**"
                        }
                    }
                ]
            }
        ],
        binaries: [
            {
                path: "/usr/local/bin/node"
            },
            {
                path: "/usr/bin/node"
            }
        ]
    },
    discord: {
        name: "discord",
        endpoints: [
            {
                host: "discord.com",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "POST",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "PUT",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "PATCH",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "DELETE",
                            path: "/api/v*/channels/*/messages/*"
                        }
                    },
                    {
                        allow: {
                            method: "DELETE",
                            path: "/api/v*/channels/*/messages/*/reactions/*/*"
                        }
                    }
                ]
            },
            {
                host: "gateway.discord.gg",
                port: 443,
                protocol: "websocket",
                enforcement: "enforce",
                websocket_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "WEBSOCKET_TEXT",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "*.discord.gg",
                port: 443,
                protocol: "websocket",
                enforcement: "enforce",
                websocket_credential_rewrite: true,
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    },
                    {
                        allow: {
                            method: "WEBSOCKET_TEXT",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "cdn.discordapp.com",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    }
                ]
            },
            {
                host: "media.discordapp.net",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/**"
                        }
                    }
                ]
            }
        ],
        binaries: [
            {
                path: "/usr/local/bin/node"
            },
            {
                path: "/usr/bin/node"
            }
        ]
    },
    telegram_bot: {
        name: "telegram_bot",
        endpoints: [
            {
                host: "api.telegram.org",
                port: 443,
                protocol: "rest",
                enforcement: "enforce",
                rules: [
                    {
                        allow: {
                            method: "GET",
                            path: "/bot*/**"
                        }
                    },
                    {
                        allow: {
                            method: "POST",
                            path: "/bot*/**"
                        }
                    },
                    {
                        allow: {
                            method: "GET",
                            path: "/file/bot*/**"
                        }
                    }
                ]
            }
        ],
        binaries: [
            {
                path: "/usr/bin/node"
            }
        ]
    }
};
const DEFAULT_MCP_URL = "http://mcp-server.aible.svc.cluster.local:8000";
// ── Component ──────────────────────────────────────────────────────────
const OpenShellCreate = (props) => {
    const accountId = useSelector((state) => accountsReducer.getSelectedAccountId(state));
    const [openShellClient, setOpenShellClient] = useState();
    useEffect(() => {
        AibleClientFactory.getInstance()
            .getAccountClient()
            .getAgentClientFactory(accountId)
            .then((acf) => setOpenShellClient(acf.getOpenShellClient()));
    }, [accountId]);
    if (openShellClient === undefined) {
        return React.createElement("div", null);
    }
    return React.createElement(OpenShellCreateComponent, { openShellClient: openShellClient, ...props });
};
const OpenShellCreateComponent = ({ onLaunched, initialConfig, openShellClient }) => {
    const dispatch = useDispatch();
    const accountId = useSelector((state) => accountsReducer.getSelectedAccountId(state));
    const selectedSandbox = useSelector((state) => chatGPTReducer.getSelectedSandbox(state));
    const folderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const chatTone = useSelector((state) => chatGPTReducer.getChatTone(state));
    const eligibleSkills = useSelector(selectEligibleSkills);
    const missingRequirements = useSelector(selectMissingRequirements);
    // Sandbox
    const [name, setName] = useState(selectedSandbox?.name ?? initialConfig?.name ?? "");
    const [existingNames, setExistingNames] = useState(new Set());
    const isOpenClawConnected = useSelector((state) => selectIsOpenClawConnected(state));
    const [skillsOptions, updateSkillsOptions] = useState([]);
    const [isSkillsLoading, updateIsSkillsLoading] = useState(false);
    const fetchSkillsCatalog = async () => {
        updateIsSkillsLoading(true);
        try {
            const skillsResp = await openShellClient.getSkillsCatalog();
            updateSkillsOptions(skillsResp?.skills ?? []);
        }
        catch (e) {
            console.log("Couldn't fetch skills catalog:", e);
        }
        finally {
            updateIsSkillsLoading(false);
        }
    };
    useEffect(() => {
        fetchSkillsCatalog();
    }, []);
    useEffect(() => {
        openShellClient
            .getSandboxes()
            .then((sandboxes) => setExistingNames(new Set(sandboxes.map((s) => s.name?.toLowerCase()).filter(Boolean))));
    }, []);
    useEffect(() => {
        if (selectedSandbox !== undefined && isOpenClawConnected !== true) {
            dispatch(connectToOpenClaw(selectedSandbox?.gateway_url, selectedSandbox?.gateway_token));
        }
    }, []);
    // Cancel any in-flight gateway health poll when the component unmounts.
    useEffect(() => {
        return () => {
            if (gatewayPollTimerRef.current !== null) {
                clearTimeout(gatewayPollTimerRef.current);
                gatewayPollTimerRef.current = null;
            }
        };
    }, []);
    const isDuplicateName = selectedSandbox === undefined && name.trim().length > 0 && existingNames.has(name.trim().toLowerCase());
    // Provider
    const aiServiceModels = useSelector((state) => aiServiceModelsReducer.getModels(state)) ?? {};
    // Build react-select options from the models array
    const modelOptions = useMemo(() => Object.keys(aiServiceModels)
        .filter((m) => aiServiceModels[m].status === "ENABLED")
        .map((m) => ({
        label: `${aiServiceModels[m].name}  (${aiServiceModels[m].external_id})`,
        value: aiServiceModels[m].id,
        model: aiServiceModels[m]
    })), [aiServiceModels]);
    const modelId = useSelector((state) => chatGPTReducer.getModelId(state));
    const matchedModel = modelOptions.find((opt) => opt.model.external_id === modelId);
    const sandboxModel = selectedSandbox !== undefined && selectedSandbox?.metadata?.model_id;
    const [selectedModel, setSelectedModel] = useState(null);
    // Track which initialConfig we've resolved against, so a new template re-resolves
    // but user changes within the same template are preserved.
    const [resolvedForConfig, setResolvedForConfig] = useState(null);
    // Resolve selected model once options are loaded.
    // Priority: sandbox > template (by external_id, fallback to id) > current chatGPT modelId
    useEffect(() => {
        if (modelOptions.length === 0)
            return;
        // Skip if we already resolved for this exact initialConfig reference
        // and the user may have made changes since
        if (resolvedForConfig === initialConfig && selectedModel)
            return;
        let resolved;
        if (sandboxModel) {
            resolved = modelOptions.find((opt) => opt.model.id === sandboxModel);
        }
        else if (initialConfig) {
            resolved =
                modelOptions.find((opt) => opt.model.external_id === initialConfig.model_external_id) ??
                    modelOptions.find((opt) => opt.model.id === initialConfig.model_id);
        }
        else {
            resolved = matchedModel;
        }
        if (resolved) {
            setSelectedModel(resolved);
            setResolvedForConfig(initialConfig ?? null);
        }
    }, [modelOptions, sandboxModel, initialConfig, matchedModel]);
    const [config, setConfig] = useState(initialConfig?.config ?? JSON.stringify({ OPENAI_BASE_URL: "" }, null, 2));
    const [endpointLoading, setEndpointLoading] = useState(false);
    const [mcpUrl, setMcpUrl] = useState(initialConfig?.mcp_url ?? DEFAULT_MCP_URL);
    // Policy
    const [policyJson, setPolicyJson] = useState(selectedSandbox?.policy_json ?? initialConfig?.policy_json ?? DEFAULT_POLICY);
    const [webSearch, setWebSearch] = useState(selectedSandbox?.web_search_json ?? initialConfig?.web_search);
    // State
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    // Tracks the active gateway-health polling timer so it can be cancelled on
    // unmount or if another launch starts before the first one completes.
    const gatewayPollTimerRef = useRef(null);
    const [isAibleSkillsOpen, setIsAibleSkillsOpen] = useState(false);
    // const sandboxAibleSkillContent = selectedSandbox?.skills?.aible;
    const sandboxPlugins = useSelector(selectPlugins);
    const sandboxAibleSkillEnabled = sandboxPlugins?.entries?.["aible-plugin"]?.enabled;
    const [aibleSkill, setAibleSkill] = useState(initialConfig?.aible_skill ?? skillsContent);
    const [useAibleSkills, setUseAibleSkills] = useState(selectedSandbox !== undefined ? sandboxAibleSkillEnabled : (initialConfig?.use_aible_skill ?? true));
    const [selectedCatalogSkills, updateSelectedCatalogSkills] = useState([]);
    const [slackConfig, setSlackConfig] = useState();
    const [discordConfig, setDiscordConfig] = useState();
    const [telegramConfig, setTelegramConfig] = useState();
    const [channelProviderCounts, setChannelProviderCounts] = useState({ enabled: 0, total: 0 });
    const handleChannelProviderCountsChange = useCallback((counts) => {
        setChannelProviderCounts(counts);
    }, []);
    const [selectedProviders, setSelectedProviders] = useState([]);
    // Re-sync local state when initialConfig changes (e.g., user switches templates)
    useEffect(() => {
        if (!initialConfig)
            return;
        // Don't override if we're editing an existing sandbox
        if (selectedSandbox !== undefined)
            return;
        if (initialConfig.name !== undefined)
            setName(initialConfig.name);
        if (initialConfig.config !== undefined)
            setConfig(initialConfig.config);
        if (initialConfig.mcp_url !== undefined)
            setMcpUrl(initialConfig.mcp_url);
        if (initialConfig.policy_json !== undefined)
            setPolicyJson(initialConfig.policy_json);
        if (initialConfig.aible_skill !== undefined && initialConfig.aible_skill !== null) {
            setAibleSkill(initialConfig.aible_skill);
        }
        if (initialConfig.use_aible_skill !== undefined)
            setUseAibleSkills(initialConfig.use_aible_skill);
        if (initialConfig.chat_tone !== undefined) {
            dispatch(chatGPTReducer.updateChatTone(initialConfig.chat_tone));
        }
        if (initialConfig.web_search !== undefined) {
            setWebSearch(initialConfig.web_search);
        }
    }, [initialConfig, selectedSandbox, dispatch]);
    // Update useAibleSkills after sandboxConfig is loaded from websocket
    useEffect(() => {
        if (selectedSandbox && sandboxPlugins) {
            setUseAibleSkills(sandboxAibleSkillEnabled);
        }
    }, [selectedSandbox, sandboxPlugins]);
    const handleModelChange = useCallback(async (option) => {
        setSelectedModel(option);
        if (option?.model?.external_id) {
            dispatch(chatGPTReducer.updateModelId(option?.model?.external_id));
        }
        if (!option) {
            setConfig(JSON.stringify({ OPENAI_BASE_URL: "" }, null, 2));
            return;
        }
        // Fetch the connection to get the endpoint URL
        setEndpointLoading(true);
        try {
            const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
            const sConnection = (await factory
                .getContentGenerationClient()
                .getConnectionById(option.model.connection_id));
            const endpoint = sConnection?.config?.endpoint ?? "";
            setConfig(JSON.stringify({ OPENAI_BASE_URL: endpoint + "/v1" }, null, 2));
        }
        catch (err) {
            console.error("Failed to fetch connection endpoint:", err);
            setConfig(JSON.stringify({ OPENAI_BASE_URL: "" }, null, 2));
        }
        finally {
            setEndpointLoading(false);
        }
    }, [accountId]);
    const constructLaunchRequest = async (model_id, isUseAibleSkills, webSearch, slackConfig, discordConfig, telegramConfig, providers) => {
        const collectionId = await dispatch(chatGPTReducer.createDefaultCollection());
        const builder = new OpenShellAgentBuilder(folderId, model_id)
            .addCollectionId(collectionId)
            .addMCPUrl(mcpUrl)
            .addApiKey(getAPIToken() ?? null)
            .addSkillRefs(selectedCatalogSkills)
            .addSlackConfig(slackConfig)
            .addDiscordConfig(discordConfig)
            .addTelegramConfig(telegramConfig);
        for (const provider of providers ?? []) {
            builder.addProvider(provider);
        }
        if (isUseAibleSkills) {
            builder.addSkill("aible", aibleSkill);
        }
        let retPolicy = policyJson;
        if (typeof retPolicy === "string") {
            try {
                retPolicy = JSON.parse(policyJson);
            }
            catch {
                console.log("Error parsing policyJSON");
            }
        }
        if (slackConfig) {
            retPolicy = {
                ...retPolicy,
                network_policies: {
                    ...retPolicy.network_policies,
                    slack: CHANNEL_PROFILES.slack
                }
            };
        }
        if (discordConfig) {
            retPolicy = {
                ...retPolicy,
                network_policies: {
                    ...retPolicy.network_policies,
                    discord: CHANNEL_PROFILES.discord
                }
            };
        }
        if (telegramConfig) {
            retPolicy = {
                ...retPolicy,
                network_policies: {
                    ...retPolicy.network_policies,
                    telegram_bot: CHANNEL_PROFILES.telegram_bot
                }
            };
        }
        if (Object.keys(retPolicy).length > 0) {
            builder.addPolicyJson(retPolicy);
        }
        const body = builder.buildLaunchRequest(name.trim(), { useAibleSkill: isUseAibleSkills }, { webSearch: webSearch, useAibleSkill: isUseAibleSkills, useAibleSkillWorkflow: true });
        return body;
    };
    const handleLaunch = async (isUseAibleSkills, webSearch) => {
        setError(null);
        setResult(null);
        if (!selectedModel) {
            setError("Please select a model.");
            return;
        }
        const body = await constructLaunchRequest(selectedModel.value, isUseAibleSkills, webSearch, slackConfig, discordConfig, telegramConfig, selectedProviders);
        // Cancel any previous poll that might still be running.
        if (gatewayPollTimerRef.current !== null) {
            clearTimeout(gatewayPollTimerRef.current);
            gatewayPollTimerRef.current = null;
        }
        setIsLoading(true);
        try {
            // POST /agents/launch returns immediately; the sandbox initialises in the
            // background on the control plane. Poll silently (no Redux updates) until
            // gateway_healthy is true, then dispatch the same sequence as the original
            // synchronous launch so the rest of the UI sees a single atomic event.
            const initial = await openShellClient.createSandbox(body);
            const sandboxName = initial.name;
            // Poll until the gateway is ready, collecting the final full health snapshot.
            const POLL_INTERVAL_MS = 2000;
            const POLL_TIMEOUT_MS = 5 * 60 * 1000;
            const deadline = Date.now() + POLL_TIMEOUT_MS;
            const finalHealth = await new Promise((resolve, reject) => {
                if (initial.gateway_healthy === true) {
                    resolve(initial);
                    return;
                }
                const tick = async () => {
                    if (Date.now() >= deadline) {
                        reject(new Error("Sandbox gateway did not become ready within 5 minutes."));
                        return;
                    }
                    try {
                        const health = await openShellClient.getSandbox(sandboxName);
                        if (health.gateway_healthy === true) {
                            resolve(health);
                        }
                        else {
                            gatewayPollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
                        }
                    }
                    catch (err) {
                        // ERR_NETWORK_CHANGED and other transient network failures surface as
                        // TypeError from the fetch API. Retry rather than abort — the sandbox
                        // is still initialising and a single dropped poll shouldn't kill the flow.
                        if (err instanceof TypeError && Date.now() < deadline) {
                            gatewayPollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
                        }
                        else {
                            reject(err);
                        }
                    }
                };
                gatewayPollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            });
            // From here: identical to the original synchronous launch sequence.
            setResult(finalHealth);
            dispatch(chatGPTReducer.selectBlueprint(chatGPTReducer.OPENSHELL.id));
            dispatch(chatGPTReducer.updateSelectedSandbox(finalHealth));
            dispatch(chatGPTReducer.initChatApp(chatGPTReducer.OPENSHELL, undefined, undefined, {
                reportLabel: `${finalHealth?.name} New Session`
            }));
            onLaunched?.(finalHealth);
        }
        catch (err) {
            let errorText = "Launch request failed.";
            if (typeof err?.message === "string") {
                errorText = err?.message;
            }
            else if (err?.message?.detail) {
                errorText = err?.message?.detail;
            }
            setError(errorText);
        }
        finally {
            gatewayPollTimerRef.current = null;
            setIsLoading(false);
        }
    };
    const updateSandbox = async (isUseAibleSkills, webSearch) => {
        setError(null);
        setResult(null);
        if (!selectedModel) {
            setError("Please select a model.");
            return;
        }
        const body = await constructLaunchRequest(selectedModel.value, isUseAibleSkills, webSearch, slackConfig, discordConfig, telegramConfig);
        setIsLoading(true);
        try {
            const sandboxHealthResponse = await openShellClient.updateSandbox(body);
            dispatch(chatGPTReducer.updateSelectedSandbox(sandboxHealthResponse));
        }
        catch (err) {
            let errorText = "Update request failed.";
            if (typeof err?.message === "string") {
                errorText = err?.message;
            }
            else if (err?.message?.detail) {
                errorText = err?.message?.detail;
            }
            setError(errorText);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSaveAsTemplate = useCallback(() => {
        let normalizedPolicy = policyJson;
        if (typeof normalizedPolicy === "string") {
            try {
                normalizedPolicy = JSON.parse(policyJson);
            }
            catch {
                normalizedPolicy = {};
            }
        }
        const bpConfig = {
            name: name.trim(),
            model_id: selectedModel?.value ?? null,
            model_external_id: selectedModel?.model.external_id ?? null,
            config,
            mcp_url: mcpUrl,
            policy_json: normalizedPolicy,
            use_aible_skill: useAibleSkills,
            aible_skill: useAibleSkills ? aibleSkill : null,
            chat_tone: chatTone,
            folder_id: folderId
        };
        dispatch(blueprintReducer.saveOpenShellBlueprint(bpConfig));
    }, [name, selectedModel, config, mcpUrl, policyJson, useAibleSkills, aibleSkill, chatTone, folderId, dispatch]);
    const isBraveAPIKeySetting = useSelector((state) => chatGPTReducer.getIsBraveAPIKeySetting(state));
    return (React.createElement("div", { className: styles.container },
        selectedSandbox?.name === undefined && (React.createElement("div", null,
            React.createElement(Button, { isLoading: isLoading, onClick: () => handleLaunch(useAibleSkills, webSearch), disabled: isDuplicateName, type: "primary", style: { marginBottom: "10px" } }, "Launch Agent"),
            isLoading && (React.createElement("p", { style: {
                    fontSize: 13,
                    color: "#6b7280",
                    marginBottom: 10,
                    fontStyle: "italic",
                    textAlign: "center"
                } }, "This may take up to a minute. Aible will automatically create your OpenClaw agent in OpenShell based on your selected settings.")))),
        error && React.createElement("div", { className: styles.error }, error),
        result && (React.createElement("div", { className: styles.success },
            React.createElement("strong", null, "Agent launched!"),
            React.createElement("div", { className: styles.successDetail },
                "name: ",
                result?.name,
                React.createElement("br", null),
                "ws: ",
                result?.gateway_url))),
        selectedSandbox?.name === undefined && (React.createElement(ClawTaskItem, { type: "AGENT_DEFINITION", defaultOpen: true },
            React.createElement("div", { className: styles.section },
                React.createElement("div", { className: styles.field },
                    React.createElement("label", { className: styles.label },
                        "Name ",
                        React.createElement("span", { className: styles.labelHint }, "(optional)")),
                    React.createElement("input", { className: styles.input, value: name, onChange: (e) => setName(e.target.value), placeholder: "auto-generated if empty", style: isDuplicateName ? { borderColor: "#f59e0b" } : undefined }),
                    isDuplicateName && (React.createElement("p", { style: {
                            fontSize: 12,
                            color: "#d97706",
                            marginTop: 4,
                            display: "flex",
                            alignItems: "center",
                            gap: 4
                        } },
                        "\u26A0\uFE0F An agent with the name \"",
                        name.trim(),
                        "\" already exists. Please choose another name.")))),
            React.createElement("div", { className: styles.section, style: { marginTop: "10px" } },
                React.createElement("div", { className: styles.field },
                    React.createElement("label", { className: styles.label }, "Model"),
                    React.createElement(Select, { options: modelOptions, value: selectedModel, onChange: handleModelChange, placeholder: "Select a model\u2026", isClearable: true, styles: selectStyles }))))),
        selectedSandbox?.name !== undefined && isBraveAPIKeySetting === true && (React.createElement(ClawTaskItem, { type: "API_KEY", defaultOpen: true },
            React.createElement("div", null,
                React.createElement("div", null, "Enter Brave API Key"),
                React.createElement("input", { value: webSearch?.braveApiKey, onChange: (e) => setWebSearch({ ...(webSearch ?? {}), braveApiKey: e?.currentTarget?.value }), style: { width: "100%" } })),
            React.createElement(Button, { isLoading: isLoading, onClick: () => handleLaunch(useAibleSkills, webSearch), disabled: isDuplicateName || webSearch?.braveApiKey === undefined, type: "primary", style: { marginTop: "10px" } }, "Create New Agent"))),
        selectedSandbox?.name !== undefined && React.createElement(SandboxWorkList, null),
        selectedSandbox?.name !== undefined && (React.createElement(ClawTaskItem, { type: "FLAGGED_NETWORK_REQUESTS", defaultOpen: true },
            React.createElement(FlaggedNetworkRequests, { sandboxName: selectedSandbox?.name }))),
        selectedSandbox?.name !== undefined && sandboxAibleSkillEnabled && (React.createElement(ClawTaskItem, { type: "AIBLE_TOOLS", defaultOpen: true },
            React.createElement(AibleToolsToggle, { selectedSandbox: selectedSandbox }))),
        selectedSandbox?.name !== undefined && (React.createElement(ClawTaskItem, { type: "AGENT_DEFINITION", defaultOpen: false },
            React.createElement(AgentFilesEditor, { selectedSandbox: selectedSandbox }),
            React.createElement("div", { className: styles.section },
                React.createElement("div", { className: styles.field },
                    React.createElement("label", { className: styles.label },
                        "Name ",
                        React.createElement("span", { className: styles.labelHint }, "(optional)")),
                    React.createElement("input", { className: styles.input, value: name, onChange: (e) => setName(e.target.value), placeholder: "auto-generated if empty", style: isDuplicateName ? { borderColor: "#f59e0b" } : undefined, disabled: selectedSandbox?.name !== undefined }),
                    isDuplicateName && (React.createElement("p", { style: {
                            fontSize: 12,
                            color: "#d97706",
                            marginTop: 4,
                            display: "flex",
                            alignItems: "center",
                            gap: 4
                        } },
                        "\u26A0\uFE0F An agent with the name \"",
                        name.trim(),
                        "\" already exists. Please choose another name.")))),
            React.createElement("div", { className: styles.section, style: { marginTop: "10px" } },
                React.createElement("div", { className: styles.field },
                    React.createElement("label", { className: styles.label }, "Model"),
                    React.createElement(Select, { isDisabled: true, options: modelOptions, value: selectedModel, onChange: handleModelChange, placeholder: "Select a model\u2026", isClearable: true, styles: selectStyles }))))),
        selectedSandbox?.name !== undefined && (React.createElement(ClawTaskItem, { type: "SKILLS" },
            React.createElement(SkillsList, { skills: eligibleSkills, skillsOptions: skillsOptions, isSkillsLoading: isSkillsLoading }))),
        selectedSandbox?.name !== undefined && (React.createElement(ClawTaskItem, { type: "REQUIRED_API_KEYS" },
            React.createElement(RequiredApiKeys, { missingRequirements: missingRequirements }))),
        selectedSandbox?.name !== undefined && (React.createElement(React.Fragment, null,
            React.createElement(ClawTaskItem, { type: "USAGE_STATS" },
                React.createElement(UsageStats, null)))),
        React.createElement(ClawTaskItem, { type: "AIBLE_SKILL", defaultOpen: selectedSandbox?.name === undefined, titleOverride: selectedSandbox?.name !== undefined
                ? !sandboxAibleSkillEnabled
                    ? "Aible Skills Disabled"
                    : "Aible Skills Enabled"
                : undefined },
            React.createElement("div", { className: styles.clawTableToggle },
                React.createElement(ButtonGroup, null,
                    React.createElement(Button, { active: !useAibleSkills, onClick: () => setUseAibleSkills(false), disabled: selectedSandbox?.name !== undefined && sandboxAibleSkillEnabled, className: styles.clawTableButton, style: { width: "49%" } },
                        React.createElement("div", { className: selectedSandbox?.name !== undefined && sandboxAibleSkillEnabled
                                ? styles.clawTable + " " + styles.clawTableDisabled
                                : styles.clawTable },
                            React.createElement("div", { className: styles.clawTableHeader },
                                React.createElement("div", null, "AibleClaw Extending NemoClaw"),
                                React.createElement("div", { className: "font-normal" }, "No Aible Skills")),
                            React.createElement("div", null,
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Business User Friendly UI by Aible")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Workspace Secured by NVIDIA OpenShell")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Save to agent templates for consistent execution without duplicating steps")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Portable across users and platforms")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faBan })),
                                    React.createElement("div", null, "Ad hoc data analysis subject to inconsistency & hallucination")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faBan })),
                                    React.createElement("div", null, "Predictive agents not aligned to business strategies and constraints"))))),
                    React.createElement(Button, { active: useAibleSkills, onClick: () => {
                            setUseAibleSkills(true);
                        }, disabled: selectedSandbox?.name !== undefined && !sandboxAibleSkillEnabled, className: styles.clawTableButton, style: { width: "51%" } },
                        React.createElement("div", { className: selectedSandbox?.name !== undefined && !sandboxAibleSkillEnabled
                                ? styles.clawTable + " " + styles.clawTableDisabled
                                : styles.clawTable },
                            React.createElement("div", { className: styles.clawTableHeader },
                                React.createElement("div", null, "AibleClaw Extending NemoClaw"),
                                React.createElement("div", { className: "font-semibold" }, "+ Aible Skills")),
                            React.createElement("div", null,
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Business User Friendly UI by Aible")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Workspace Secured by NVIDIA OpenShell")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Save to agent templates for consistent execution without duplicating steps")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck })),
                                    React.createElement("div", null, "Portable across users and platforms")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck, style: { color: "#5925DC" } })),
                                    React.createElement("div", { style: { fontWeight: "bold" } }, "Agent-first analysis with consistent, verified results using Aible's “Blue Is True” deterministic double-check")),
                                React.createElement("div", { className: styles.clawTableItem },
                                    React.createElement("div", { className: styles.clawTableIcon },
                                        React.createElement(FontAwesomeIcon, { icon: faCheck, style: { color: "#5925DC" } })),
                                    React.createElement("div", { style: { fontWeight: "bold" } }, "Aligned to business KPIs, constraints and strategies"))))))),
            useAibleSkills && (React.createElement(Accordion, { onToggle: () => setIsAibleSkillsOpen(!isAibleSkillsOpen), header: React.createElement("div", { className: styles.skillsToggleLeft, style: { marginTop: "20px" } },
                    React.createElement("div", { className: styles.skillsIcon },
                        React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" },
                            React.createElement("path", { d: "M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z", stroke: "currentColor", strokeWidth: "1.3", fill: "none" }),
                            React.createElement("path", { d: "M5 7h6M5 9.5h4", stroke: "currentColor", strokeWidth: "1.3", strokeLinecap: "round" }))),
                    React.createElement("div", null,
                        React.createElement("div", { className: styles.skillsLabel }, useAibleSkills ? "Aible Skill" : "Not Using Aible Skill"))), show: isAibleSkillsOpen },
                React.createElement("div", null,
                    React.createElement("div", { className: styles.editorPanel },
                        React.createElement("div", { className: styles.editorToolbar },
                            React.createElement("span", { className: styles.editorBadge },
                                "Markdown",
                                React.createElement("span", { className: styles.editorDot, "data-has-content": !!aibleSkill })),
                            React.createElement("button", { className: styles.editorCopy, onClick: () => navigator.clipboard?.writeText(aibleSkill) }, "Copy")),
                        React.createElement("textarea", { className: styles.editorTextarea, value: aibleSkill, onChange: (e) => setAibleSkill(e.currentTarget.value), placeholder: "Paste your skill markdown here...", spellCheck: false })),
                    React.createElement("div", { style: { marginTop: "20px" } },
                        React.createElement("div", { className: "mb-[10px]" },
                            React.createElement("input", { className: styles.input, value: mcpUrl, onChange: (e) => setMcpUrl(e.target.value), placeholder: "MCP Url", style: { width: "100%" } }))))))),
        selectedSandbox?.name === undefined && (React.createElement(ClawTaskItem, { type: "SKILLS" },
            React.createElement(SkillsCatalog, { selectedProviders: selectedProviders, updateSelectedProviders: setSelectedProviders, openShellClient: openShellClient, skillsOptions: skillsOptions, isSkillsLoading: isSkillsLoading, selectedSkills: selectedCatalogSkills, updateSkills: updateSelectedCatalogSkills }))),
        React.createElement(ClawTaskItem, { type: "AGENT_CHANNELS", defaultOpen: selectedSandbox?.name === undefined, headerActions: channelProviderCounts.total > 0 ? (React.createElement("span", { className: "ml-auto text-xs text-gray-500 whitespace-nowrap" },
                channelProviderCounts.enabled,
                " of ",
                channelProviderCounts.total,
                " enabled")) : null },
            React.createElement(ChannelsList, { slack: slackConfig, setSlack: setSlackConfig, discord: discordConfig, setDiscord: setDiscordConfig, telegram: telegramConfig, setTelegram: setTelegramConfig, openShellClient: openShellClient, onProviderCountsChange: handleChannelProviderCountsChange })),
        React.createElement(ClawTaskItem, { type: "AGENT_PROVIDERS" },
            React.createElement(ProvidersList, { providers: selectedProviders, updateProviders: setSelectedProviders, openShellClient: openShellClient })),
        React.createElement("div", { className: styles.section },
            React.createElement(PolicyEditor, { value: policyJson, onChange: setPolicyJson, webSearch: webSearch, onChangeWebSearch: setWebSearch, openShellClient: openShellClient })),
        selectedSandbox?.name !== undefined && (React.createElement("div", { className: styles.footer },
            React.createElement(Button, { isLoading: isLoading, onClick: () => {
                    updateSandbox(useAibleSkills, webSearch);
                    console.log("Updating agent: ", selectedSandbox?.name);
                }, disabled: isDuplicateName }, "Update Agent"),
            React.createElement(Button, { onClick: handleSaveAsTemplate, disabled: !selectedModel }, "Save as Template")))));
};
// =============================================================
// AibleToolsToggle — enable / disable Aible tools on the live gateway
// =============================================================
const AibleToolsToggle = (props) => {
    const dispatch = useDispatch();
    const toolsDeny = useSelector(selectToolsDeny);
    const isConnected = useSelector(selectIsOpenClawConnected);
    const connectionStatus = useSelector(selectOpenClawConnectionStatus);
    const [localDeny, setLocalDeny] = useState(toolsDeny);
    const [applying, setApplying] = useState(false);
    const [restarting, setRestarting] = useState(false);
    const selectedSandbox = props?.selectedSandbox;
    // Keep local state in sync when the store changes from outside (e.g. initial load,
    // or a successful apply that updated the store).
    useEffect(() => {
        setLocalDeny(toolsDeny);
    }, [toolsDeny]);
    useEffect(() => {
        if (connectionStatus === "connected" && restarting) {
            setRestarting(false);
        }
    }, [connectionStatus, restarting]);
    const isRestarting = restarting || connectionStatus === "reconnecting";
    // Compare against server state to determine if there are unsaved changes.
    const isDirty = useMemo(() => {
        const server = toolsDeny;
        if (server.length !== localDeny.length)
            return true;
        const serverSet = new Set(server);
        return localDeny.some((t) => !serverSet.has(t));
    }, [localDeny, toolsDeny]);
    const handleToggle = (tool) => {
        if (!isConnected || applying || isRestarting)
            return;
        setLocalDeny((prev) => (prev.includes(tool) ? prev.filter((t) => t !== tool) : [...prev, tool]));
    };
    const handleApply = async () => {
        if (!isDirty || applying || isRestarting || !isConnected)
            return;
        try {
            setApplying(true);
            await dispatch(patchOpenClawToolsDeny(localDeny));
            setRestarting(true);
            dispatch(disconnectFromOpenClaw());
            dispatch(connectToOpenClaw(selectedSandbox?.gateway_url, selectedSandbox?.gateway_token));
        }
        finally {
            setApplying(false);
        }
    };
    const handleReset = () => {
        if (applying || isRestarting)
            return;
        setLocalDeny(toolsDeny);
    };
    const enabledCount = AIBLE_TOOLS.length - localDeny.length;
    const applyDisabled = !isDirty || applying || isRestarting || !isConnected;
    return (React.createElement("div", { className: "flex flex-col gap-3" },
        React.createElement("div", { className: "flex items-center gap-2" },
            React.createElement("span", { className: "ml-auto text-xs text-gray-500" },
                enabledCount,
                " of ",
                AIBLE_TOOLS.length,
                " enabled")),
        isRestarting && (React.createElement("p", { className: "text-xs text-amber-600 font-medium" }, "\u27F3 Gateway restarting \u2014 toggles will re-enable once reconnected.")),
        !isConnected && !isRestarting && React.createElement("p", { className: "text-xs text-amber-600" }, "Connect to a sandbox to manage tools."),
        React.createElement("ul", { className: "flex flex-col gap-2" }, AIBLE_TOOLS.map((tool) => {
            const isDenied = localDeny.includes(tool);
            return (React.createElement("li", { key: tool, className: "flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5", style: { opacity: isRestarting ? 0.5 : 1 } },
                React.createElement("div", { className: "flex-1 min-w-0" },
                    React.createElement("p", { className: "text-sm font-medium font-mono" }, tool)),
                React.createElement(Toggle, { checked: !isDenied, onChange: () => handleToggle(tool), label: isDenied ? `Enable ${tool}` : `Disable ${tool}`, disabled: isRestarting || applying || !isConnected })));
        })),
        React.createElement("div", { className: "flex items-center justify-end gap-2 pt-1" },
            isDirty && !applying && !isRestarting && (React.createElement("button", { type: "button", onClick: handleReset, className: "text-xs text-gray-600 hover:text-gray-900 px-2 py-1" }, "Reset")),
            React.createElement("button", { type: "button", onClick: handleApply, disabled: applyDisabled, className: `text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${applyDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}` }, applying ? "Applying…" : isRestarting ? "Restarting…" : "Apply"))));
};
// =============================================================
// UsageStats — sessions.usage WebSocket panel
// =============================================================
function UsageStats() {
    const dispatch = useDispatch();
    const isConnected = useSelector(selectIsOpenClawConnected);
    const usage = useSelector(selectSessionsUsage);
    useEffect(() => {
        if (isConnected && usage === null) {
            dispatch(fetchSessionsUsage());
        }
    }, [isConnected]);
    const totals = useMemo(() => {
        if (!usage?.sessions?.length)
            return null;
        return usage.sessions.reduce((acc, s) => ({
            messages: acc.messages + (s?.usage?.messageCounts?.total ?? 0),
            toolCalls: acc.toolCalls + (s?.usage?.messageCounts?.toolCalls ?? 0),
            inputTokens: acc.inputTokens + (s?.usage?.input ?? 0),
            outputTokens: acc.outputTokens + (s?.usage?.output ?? 0),
            totalTokens: acc.totalTokens + (s?.usage?.totalTokens ?? 0),
            sessionCount: acc.sessionCount + 1
        }), { messages: 0, toolCalls: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0, sessionCount: 0 });
    }, [usage]);
    if (!isConnected) {
        return React.createElement("p", { style: { color: "#888", fontSize: 13 } }, "Connect to the gateway to view usage stats.");
    }
    if (usage === null) {
        return React.createElement("p", { style: { color: "#888", fontSize: 13 } }, "Loading usage stats\u2026");
    }
    const statRowStyle = {
        display: "flex",
        justifyContent: "space-between",
        padding: "6px 0",
        borderBottom: "1px solid #f0eef8",
        fontSize: 13
    };
    const labelStyle = { color: "#6b6585" };
    const valueStyle = { fontWeight: 600, color: "#1a1630" };
    return (React.createElement("div", { style: { padding: "8px 0" } },
        React.createElement("div", { style: statRowStyle },
            React.createElement("span", { style: labelStyle }, "Sessions"),
            React.createElement("span", { style: valueStyle }, (totals?.sessionCount ?? 0).toLocaleString())),
        React.createElement("div", { style: statRowStyle },
            React.createElement("span", { style: labelStyle }, "Messages"),
            React.createElement("span", { style: valueStyle }, (totals?.messages ?? 0).toLocaleString())),
        React.createElement("div", { style: statRowStyle },
            React.createElement("span", { style: labelStyle }, "Tool calls"),
            React.createElement("span", { style: valueStyle }, (totals?.toolCalls ?? 0).toLocaleString())),
        React.createElement("div", { style: statRowStyle },
            React.createElement("span", { style: labelStyle }, "Input tokens"),
            React.createElement("span", { style: valueStyle }, (totals?.inputTokens ?? 0).toLocaleString())),
        React.createElement("div", { style: statRowStyle },
            React.createElement("span", { style: labelStyle }, "Output tokens"),
            React.createElement("span", { style: valueStyle }, (totals?.outputTokens ?? 0).toLocaleString())),
        React.createElement("div", { style: { ...statRowStyle, borderBottom: "none" } },
            React.createElement("span", { style: labelStyle }, "Total tokens"),
            React.createElement("span", { style: valueStyle }, (totals?.totalTokens ?? 0).toLocaleString())),
        React.createElement("button", { onClick: () => dispatch(fetchSessionsUsage()), style: {
                marginTop: 10,
                fontSize: 12,
                color: "#7c5cfc",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0
            } }, "Refresh")));
}
// =============================================================
const AGENT_FILE_NAMES = ["SOUL.md", "AGENTS.md", "TOOLS.md", "IDENTITY.md", "USER.md"];
const AgentFilesEditor = (props) => {
    const dispatch = useDispatch();
    const isConnected = useSelector(selectIsOpenClawConnected);
    const [activeFile, setActiveFile] = useState("SOUL.md");
    const [saving, setSaving] = useState(false);
    const content = useSelector(selectAgentFileContent(activeFile));
    const draft = useSelector(selectAgentFileDraft(activeFile));
    const isDirty = useSelector(selectAgentFileHasUnsavedChanges(activeFile));
    // Fetch file content when tab changes or connection is established
    useEffect(() => {
        if (isConnected && content === null) {
            dispatch(fetchAgentFile(activeFile));
        }
    }, [isConnected, activeFile, content]);
    const handleSave = async () => {
        if (!isDirty || draft === null)
            return;
        setSaving(true);
        await dispatch(saveAgentFile(activeFile, draft));
        dispatch(handleOpenClaw("/new", false, props?.selectedSandbox, true));
        setSaving(false);
    };
    const handleDiscard = () => {
        if (content !== null) {
            dispatch(setAgentFileDraft({ name: activeFile, content }));
        }
    };
    return (React.createElement("div", { className: "flex flex-col gap-3" },
        React.createElement("div", { style: { display: "flex", gap: "4px", borderBottom: "1px solid #e5e7eb", paddingBottom: "8px" } }, AGENT_FILE_NAMES.map((name) => (React.createElement("button", { key: name, onClick: () => setActiveFile(name), style: {
                padding: "4px 10px",
                borderRadius: "6px",
                border: "none",
                fontSize: "12px",
                fontFamily: "monospace",
                cursor: "pointer",
                backgroundColor: activeFile === name ? "#5925DC" : "transparent",
                color: activeFile === name ? "white" : "#6b7280",
                fontWeight: activeFile === name ? 600 : 400
            } }, (name ?? "").replace(".md", ""))))),
        !isConnected && React.createElement("p", { className: "text-xs text-amber-600" }, "Connect to a sandbox to edit agent files."),
        isConnected && content === null && React.createElement("p", { className: "text-xs text-gray-400" },
            "Loading ",
            activeFile,
            "\u2026"),
        isConnected && content !== null && (React.createElement(React.Fragment, null,
            React.createElement("textarea", { value: draft ?? content, onChange: (e) => dispatch(setAgentFileDraft({ name: activeFile, content: e.target.value })), spellCheck: false, style: {
                    width: "100%",
                    minHeight: "280px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    padding: "10px",
                    resize: "vertical",
                    outline: "none",
                    backgroundColor: "#fafafa",
                    color: "#111827"
                } }),
            React.createElement("div", { style: { display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" } },
                isDirty && React.createElement("span", { style: { fontSize: "11px", color: "#f59e0b" } }, "Unsaved changes"),
                React.createElement("button", { onClick: handleDiscard, disabled: !isDirty, style: {
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        background: "white",
                        fontSize: "12px",
                        cursor: isDirty ? "pointer" : "not-allowed",
                        color: isDirty ? "#374151" : "#9ca3af"
                    } }, "Discard"),
                React.createElement("button", { onClick: handleSave, disabled: !isDirty || saving, style: {
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: "none",
                        background: isDirty && !saving ? "#5925DC" : "#7344E1",
                        color: "white",
                        fontSize: "12px",
                        cursor: isDirty && !saving ? "pointer" : "not-allowed"
                    } }, saving ? "Saving…" : "Save"))))));
};
export function SkillsList({ skills, skillsOptions, isSkillsLoading }) {
    const [skillView, setSkillView] = useState("ENABLED");
    const disabledSkills = (skillsOptions ?? []).filter((skillOption) => {
        const foundSkill = skills?.find((o) => o?.name === skillOption?.id);
        if (foundSkill) {
            return false;
        }
        return true;
    });
    const enabledCount = skills?.length ?? 0;
    const totalSkillsCount = enabledCount + disabledSkills.length;
    return (React.createElement("div", { className: "flex flex-col gap-3" },
        React.createElement("div", { className: "flex items-center justify-between" },
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { onClick: () => {
                        setSkillView("ENABLED");
                    }, active: skillView === "ENABLED", isSmall: true }, "Enabled"),
                React.createElement(Button, { onClick: () => {
                        setSkillView("DISABLED");
                    }, active: skillView === "DISABLED", isSmall: true }, "Disabled")),
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("span", { className: "ml-auto text-xs text-gray-500" },
                    enabledCount,
                    " of ",
                    totalSkillsCount,
                    " enabled"))),
        skillView === "DISABLED" ? (React.createElement("ul", { className: "flex flex-col gap-2" }, isSkillsLoading ? (React.createElement("div", { className: "flex items-center justify-center" },
            React.createElement(LoadingIndicator, { style: { scale: ".6", margin: "0px" } }),
            "Loading Skills")) : (React.createElement(React.Fragment, null, disabledSkills.map((skill) => (React.createElement("li", { key: skill.id, className: "flex items-start gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
            React.createElement("div", { className: "flex-1 min-w-0" },
                React.createElement("p", { className: "text-sm font-medium" }, skill.id),
                React.createElement("p", { className: "text-sm text-gray-500 leading-snug" }, skill.description))))))))) : (React.createElement("ul", { className: "flex flex-col gap-2" }, skills.map((skill) => (React.createElement("li", { key: skill.name, className: "flex items-start gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
            React.createElement("div", { className: "flex-1 min-w-0" },
                React.createElement("p", { className: "text-sm font-medium" }, skill.name),
                React.createElement("p", { className: "text-sm text-gray-500 leading-snug" }, skill.description)))))))));
}
export function RequiredApiKeys({ missingRequirements, onChange }) {
    // Pull out only tools that are missing an env var ending in _API_KEY
    const apiKeyNeeds = useMemo(() => {
        const needs = [];
        for (const req of missingRequirements) {
            const apiKeyVar = req.missing.env.find((v) => v.endsWith("_API_KEY"));
            if (apiKeyVar)
                needs.push({ toolName: req.name, envVar: apiKeyVar });
        }
        return needs;
    }, [missingRequirements]);
    const [values, setValues] = useState({});
    const [revealed, setRevealed] = useState({});
    if (apiKeyNeeds.length === 0)
        return null;
    const filledCount = Object.values(values).filter((v) => v && v.length > 0).length;
    const handleChange = (need, value) => {
        setValues((prev) => ({ ...prev, [need.toolName]: value }));
        onChange?.(need.toolName, need.envVar, value);
    };
    return (React.createElement("div", { className: "flex flex-col gap-3" },
        React.createElement("ul", { className: "flex flex-col gap-2.5" }, apiKeyNeeds.map((need) => {
            const filled = !!values[need.toolName];
            const isRevealed = !!revealed[need.toolName];
            return (React.createElement("li", { key: need.toolName, className: "flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5" },
                React.createElement("span", { className: `h-2 w-2 shrink-0 rounded-full border ${filled ? "bg-emerald-600 border-emerald-600" : "bg-gray-100 border-gray-300"}`, "aria-label": filled ? "Key provided" : "Key missing" }),
                React.createElement("div", { className: "flex-1 min-w-0" },
                    React.createElement("p", { className: "text-sm font-medium" }, need.toolName),
                    React.createElement("p", { className: "font-mono text-xs text-gray-500" }, need.envVar)),
                React.createElement("div", { className: "relative w-60 shrink-0" },
                    React.createElement("input", { type: isRevealed ? "text" : "password", value: values[need.toolName] ?? "", onChange: (e) => handleChange(need, e.target.value), placeholder: "Paste key", autoComplete: "off", spellCheck: false, className: "w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 pr-9 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" }),
                    React.createElement("button", { type: "button", onClick: () => setRevealed((p) => ({
                            ...p,
                            [need.toolName]: !p[need.toolName]
                        })), "aria-label": isRevealed ? "Hide key" : "Show key", className: "absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100" }, isRevealed ? React.createElement(EyeOffIcon, null) : React.createElement(EyeIcon, null)))));
        }))));
}
// =============================================================
// Small primitives
// =============================================================
export function Toggle({ checked, onChange, label, disabled }) {
    return (React.createElement("button", { role: "switch", "aria-checked": checked, "aria-label": label, onClick: disabled ? undefined : onChange, disabled: disabled, style: {
            position: "relative",
            display: "inline-flex",
            width: "36px",
            height: "20px",
            borderRadius: "10px",
            border: "none",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: checked ? "#2563eb" : "#d1d5db",
            transition: "background-color 0.2s",
            flexShrink: 0,
            padding: 0
        } },
        React.createElement("span", { style: {
                position: "absolute",
                top: "2px",
                left: checked ? "18px" : "2px",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transition: "left 0.2s"
            } })));
}
function EyeIcon() {
    return (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("path", { d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" }),
        React.createElement("circle", { cx: "12", cy: "12", r: "3" })));
}
function EyeOffIcon() {
    return (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("path", { d: "M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.77 19.77 0 015.06-5.94M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.77 19.77 0 01-3.16 4.19M1 1l22 22" }),
        React.createElement("path", { d: "M14.12 14.12a3 3 0 11-4.24-4.24" })));
}
export default OpenShellCreate;
